import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Badge,
  Progress,
  Button,
  Textarea,
  Tag,
  TagLabel,
  Flex,
  Icon,
  keyframes,
  Divider,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { useScrollAnimation } from "../../hooks/useScrollAnimation";
import { FiCopy, FiZap, FiFileText, FiArrowRight, FiCheck } from "react-icons/fi";

// Smooth animation keyframes
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

// Step 1: Paste Job Description
const StepOne = ({ isActive }) => {
  const [jdText, setJdText] = useState("");
  const [showCursor, setShowCursor] = useState(false);

  const sampleJD = `Senior Frontend Engineer - TechCorp

Requirements:
• 5+ years React, TypeScript experience
• Node.js, GraphQL API development
• Docker, Kubernetes deployment
• AWS cloud platform experience
• Jest/Cypress testing frameworks`;

  useEffect(() => {
    if (!isActive) return;

    const timer = setTimeout(() => {
      setShowCursor(true);
      let i = 0;
      const typeInterval = setInterval(() => {
        if (i < sampleJD.length) {
          setJdText(sampleJD.slice(0, i + 1));
          i++;
        } else {
          clearInterval(typeInterval);
          setShowCursor(false);
        }
      }, 25);
    }, 500);

    return () => {
      clearTimeout(timer);
      setJdText("");
      setShowCursor(false);
    };
  }, [isActive]);

  return (
    <Box
      bg="gray.900"
      p={6}
      borderRadius="xl"
      border="1px solid"
      borderColor="gray.700"
      h="400px"
      position="relative"
      fontFamily="mono"
      transition="all 0.3s ease"
      _hover={{
        transform: "translateY(-2px)",
        shadow: "xl",
        borderColor: "blue.500",
      }}
    >
      {/* Terminal Header */}
      <HStack spacing={2} mb={4}>
        <Box w={3} h={3} bg="red.400" borderRadius="full" />
        <Box w={3} h={3} bg="yellow.400" borderRadius="full" />
        <Box w={3} h={3} bg="green.400" borderRadius="full" />
        <Text fontSize="sm" color="gray.400" ml={4}>
          job-description.txt
        </Text>
      </HStack>

      <VStack align="stretch" spacing={4} h="calc(100% - 40px)">
        <HStack spacing={3} mb={2}>
          <Box
            w={8}
            h={8}
            bg="blue.500"
            borderRadius="md"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Icon as={FiCopy} color="white" boxSize={4} />
          </Box>
          <VStack align="start" spacing={0}>
            <Text fontWeight="600" color="white" fontSize="sm">
              1. Paste Job Description
            </Text>
            <Text fontSize="xs" color="gray.400">
              Copy and paste the job posting
            </Text>
          </VStack>
        </HStack>

        <Box
          bg="gray.800"
          p={4}
          borderRadius="lg"
          border="1px solid"
          borderColor="gray.600"
          flex="1"
          position="relative"
          overflow="hidden"
        >
          <Text
            color="green.300"
            fontSize="sm"
            fontFamily="mono"
            lineHeight="1.6"
            whiteSpace="pre-wrap"
          >
            {jdText}
            {showCursor && (
              <Box
                as="span"
                display="inline-block"
                w="2px"
                h="16px"
                bg="green.400"
                ml="1px"
                animation="blink 1s infinite"
              />
            )}
          </Text>
        </Box>

        <Badge
          colorScheme="blue"
          variant="subtle"
          px={3}
          py={1}
          borderRadius="full"
          fontSize="xs"
          alignSelf="start"
        >
          {jdText ? "Ready for Analysis" : "SkillBack Processing Ready"}
        </Badge>
      </VStack>
    </Box>
  );
};

// Step 2: Extract Skills & Match Score
const StepTwo = ({ isActive }) => {
  const [extractedSkills, setExtractedSkills] = useState([]);
  const [matchScore, setMatchScore] = useState(0);
  const [showScore, setShowScore] = useState(false);

  const skills = [
    { name: "React", match: true },
    { name: "TypeScript", match: true },
    { name: "Node.js", match: true },
    { name: "GraphQL", match: false },
    { name: "Docker", match: true },
    { name: "Kubernetes", match: false },
    { name: "AWS", match: false },
    { name: "Jest", match: true },
  ];

  useEffect(() => {
    if (!isActive) return;

    const timer = setTimeout(() => {
      // Extract skills one by one
      skills.forEach((skill, index) => {
        setTimeout(() => {
          setExtractedSkills(prev => [...prev, skill]);
        }, index * 200);
      });

      // Show match score after skills are extracted
      setTimeout(() => {
        setShowScore(true);
        let score = 0;
        const scoreInterval = setInterval(() => {
          if (score < 72) {
            score += 3;
            setMatchScore(score);
          } else {
            clearInterval(scoreInterval);
          }
        }, 50);
      }, skills.length * 200 + 500);
    }, 300);

    return () => {
      clearTimeout(timer);
      setExtractedSkills([]);
      setMatchScore(0);
      setShowScore(false);
    };
  }, [isActive]);

  return (
    <Box
      bg="white"
      p={6}
      borderRadius="xl"
      border="1px solid"
      borderColor="gray.200"
      h="400px"
      shadow="sm"
      transition="all 0.3s ease"
      _hover={{
        transform: "translateY(-2px)",
        shadow: "xl",
        borderColor: "orange.300",
      }}
    >
      <VStack align="stretch" spacing={4} h="full">
        <HStack spacing={3}>
          <Box
            w={8}
            h={8}
            bg="orange.500"
            borderRadius="md"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Icon as={FiZap} color="white" boxSize={4} />
          </Box>
          <VStack align="start" spacing={0}>
            <Text fontWeight="600" color="gray.900" fontSize="sm">
              2. SkillBack Analysis
            </Text>
            <Text fontSize="xs" color="gray.500">
              SME-trained model extracts and scores requirements
            </Text>
          </VStack>
        </HStack>

        <VStack align="stretch" spacing={4} flex="1">
          <Box>
            <Text fontSize="xs" fontWeight="600" color="gray.600" mb={3}>
              EXTRACTED REQUIREMENTS:
            </Text>
            <Flex wrap="wrap" gap={2}>
              {extractedSkills.map((skill, index) => (
                <Tag
                  key={skill.name}
                  size="sm"
                  bg={skill.match ? "green.50" : "orange.50"}
                  color={skill.match ? "green.700" : "orange.700"}
                  border="1px solid"
                  borderColor={skill.match ? "green.200" : "orange.200"}
                  borderRadius="md"
                  animation={`${slideIn} 0.3s ease-out`}
                  animationDelay={`${index * 0.1}s`}
                  animationFillMode="both"
                >
                  <TagLabel fontSize="xs">{skill.name}</TagLabel>
                  {skill.match && (
                    <Box w={2} h={2} bg="green.400" borderRadius="full" ml={1} />
                  )}
                </Tag>
              ))}
            </Flex>
          </Box>

          <Divider />

          {showScore && (
            <VStack spacing={4} animation={`${fadeInUp} 0.5s ease-out`}>
              <Box textAlign="center">
                <Text fontSize="3xl" fontWeight="700" color="gray.900">
                  {matchScore}%
                </Text>
                <Text fontSize="sm" color="green.600" fontWeight="600">
                  STRONG MATCH
                </Text>
                <Progress
                  value={matchScore}
                  colorScheme="green"
                  size="sm"
                  borderRadius="full"
                  mt={2}
                  bg="gray.100"
                />
              </Box>

              <Badge
                colorScheme="orange"
                variant="solid"
                px={3}
                py={1}
                borderRadius="full"
                fontSize="xs"
              >
                SkillBack Expert Analysis
              </Badge>
            </VStack>
          )}
        </VStack>
      </VStack>
    </Box>
  );
};

// Step 3: Generate Resume
const StepThree = ({ isActive }) => {
  const [resumeBullets, setResumeBullets] = useState([]);
  const [showButton, setShowButton] = useState(false);

  const bullets = [
    "Built scalable React applications with TypeScript serving 100K+ users",
    "Developed GraphQL APIs with Node.js improving query performance by 40%",
    "Deployed containerized applications using Docker and Kubernetes on AWS"
  ];

  useEffect(() => {
    if (!isActive) return;

    const timer = setTimeout(() => {
      bullets.forEach((bullet, index) => {
        setTimeout(() => {
          setResumeBullets(prev => [...prev, bullet]);
        }, index * 600);
      });

      setTimeout(() => {
        setShowButton(true);
      }, bullets.length * 600 + 500);
    }, 400);

    return () => {
      clearTimeout(timer);
      setResumeBullets([]);
      setShowButton(false);
    };
  }, [isActive]);

  return (
    <Box
      bg="white"
      p={6}
      borderRadius="xl"
      border="1px solid"
      borderColor="gray.200"
      h="400px"
      shadow="sm"
      transition="all 0.3s ease"
      _hover={{
        transform: "translateY(-2px)",
        shadow: "xl",
        borderColor: "purple.300",
      }}
    >
      <VStack align="stretch" spacing={4} h="full">
        <HStack spacing={3}>
          <Box
            w={8}
            h={8}
            bg="purple.500"
            borderRadius="md"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Icon as={FiFileText} color="white" boxSize={4} />
          </Box>
          <VStack align="start" spacing={0}>
            <Text fontWeight="600" color="gray.900" fontSize="sm">
              3. Generate Resume
            </Text>
            <Text fontSize="xs" color="gray.500">
              SME-crafted bullet points for maximum impact
            </Text>
          </VStack>
        </HStack>

        <VStack align="stretch" spacing={4} flex="1">
          <Box
            bg="gray.50"
            p={4}
            borderRadius="lg"
            border="1px solid"
            borderColor="gray.200"
            flex="1"
          >
            <HStack spacing={2} mb={3}>
              <Icon as={FiFileText} color="gray.600" boxSize={4} />
              <Text fontSize="xs" fontWeight="600" color="gray.600">
                OPTIMIZED RESUME BULLETS:
              </Text>
            </HStack>
            <VStack align="stretch" spacing={3}>
              {resumeBullets.map((bullet, index) => (
                <HStack
                  key={index}
                  align="start"
                  spacing={3}
                  animation={`${fadeInUp} 0.4s ease-out`}
                  animationDelay={`${index * 0.2}s`}
                  animationFillMode="both"
                >
                  <Icon as={FiCheck} color="green.500" mt={0.5} flexShrink={0} />
                  <Text fontSize="xs" color="gray.700" lineHeight="1.4">
                    {bullet}
                  </Text>
                </HStack>
              ))}
            </VStack>
          </Box>

          {showButton && (
            <Button
              bg="gray.900"
              color="white"
              size="sm"
              borderRadius="lg"
              fontWeight="600"
              _hover={{ bg: "gray.800" }}
              _focus={{ boxShadow: "none" }}
              rightIcon={<Icon as={FiArrowRight} />}
              animation={`${fadeInUp} 0.5s ease-out`}
            >
              Generate Full Resume
            </Button>
          )}

          <Badge
            colorScheme="purple"
            variant="subtle"
            px={3}
            py={1}
            borderRadius="full"
            fontSize="xs"
            alignSelf="center"
          >
            SME + SkillBack Experts
          </Badge>
        </VStack>
      </VStack>
    </Box>
  );
};

const WorkflowDemo = () => {
  const [activeStep, setActiveStep] = useState(0);
  const { ref, isInView } = useScrollAnimation({ once: true, margin: "-100px" });

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep(prev => (prev + 1) % 3);
    }, 4000); // 4 seconds per step for smooth viewing

    return () => clearInterval(interval);
  }, []);

  return (
    <Box bg="gray.50" py={{ base: "2rem", md: "3rem" }}>
      <Container maxW="6xl" px={{ base: 4, md: 8 }}>
        <VStack spacing={{ base: "2rem", md: "2.5rem" }} ref={ref}>
          {/* Header */}
          <VStack spacing="1.5rem" textAlign="center" maxW="700px">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.5 }}
            >
              <Badge
                colorScheme="blue"
                variant="subtle"
                px={4}
                py={2}
                borderRadius="full"
                fontSize="sm"
                fontWeight="600"
              >
                Live Demo
              </Badge>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <Heading as="h2" size="xl" color="gray.900" fontWeight="700">
                See SkillBack in Action
              </Heading>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Text
                color="gray.600"
                fontSize="lg"
                lineHeight="1.6"
              >
                Watch how our SME-trained model analyzes job descriptions and
                generates expert-level resume content in seconds
              </Text>
            </motion.div>
          </VStack>

          {/* Three-Step Workflow */}
          <SimpleGrid
            columns={{ base: 1, lg: 3 }}
            spacing={{ base: "2rem", lg: "2rem" }}
            w="full"
            maxW="1200px"
          >
            <StepOne isActive={activeStep === 0} />
            <StepTwo isActive={activeStep === 1} />
            <StepThree isActive={activeStep === 2} />
          </SimpleGrid>

          {/* Step Indicators */}
          <HStack spacing={8} justify="center">
            {[
              { label: "Paste JD", step: 0 },
              { label: "SME Analysis", step: 1 },
              { label: "Generate Resume", step: 2 }
            ].map(({ label, step }) => (
              <VStack key={step} spacing={2}>
                <Box
                  w={4}
                  h={4}
                  borderRadius="full"
                  bg={activeStep === step ? "blue.500" : "gray.300"}
                  transition="all 0.3s ease"
                  shadow={activeStep === step ? "lg" : "none"}
                />
                <Text
                  fontSize="xs"
                  color={activeStep === step ? "blue.600" : "gray.500"}
                  fontWeight={activeStep === step ? "600" : "400"}
                  transition="all 0.3s ease"
                >
                  {label}
                </Text>
              </VStack>
            ))}
          </HStack>

          {/* CTA */}
          <Box textAlign="center" pt={6}>
            <VStack spacing={4}>
              <VStack spacing={2}>
                <Text fontSize="lg" color="gray.700" fontWeight="600">
                  Ready to optimize your resume?
                </Text>
                <Text fontSize="sm" color="gray.500">
                  Join thousands of professionals using SkillBack
                </Text>
              </VStack>
              <HStack spacing={4}>
                <Button
                  colorScheme="blue"
                  size="lg"
                  px={8}
                  py={6}
                  fontSize="lg"
                  fontWeight="600"
                  borderRadius="xl"
                  rightIcon={<Icon as={FiArrowRight} />}
                  _hover={{
                    transform: "translateY(-2px)",
                    shadow: "xl",
                  }}
                  _focus={{ boxShadow: "none" }}
                  transition="all 0.2s ease"
                >
                  Try SkillBack Free
                </Button>
                <Button
                  variant="outline"
                  colorScheme="gray"
                  size="lg"
                  px={6}
                  py={6}
                  fontSize="lg"
                  fontWeight="500"
                  borderRadius="xl"
                  _hover={{
                    bg: "gray.50",
                  }}
                  _focus={{ boxShadow: "none" }}
                >
                  Learn More
                </Button>
              </HStack>
              <HStack spacing={6} color="gray.500" fontSize="sm" mt={4}>
                <HStack spacing={1}>
                  <Icon as={FiCheck} color="green.500" />
                  <Text>No credit card required</Text>
                </HStack>
                <HStack spacing={1}>
                  <Icon as={FiCheck} color="green.500" />
                  <Text>Free trial</Text>
                </HStack>
                <HStack spacing={1}>
                  <Icon as={FiCheck} color="green.500" />
                  <Text>Cancel anytime</Text>
                </HStack>
              </HStack>
            </VStack>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

export default WorkflowDemo;